import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EntitlementContext = createContext(null);

// Default state for when not authenticated or loading
const DEFAULT_STATE = {
  entitled_features: [],
  entitled_slugs: [],
  limits: { job_limit: 10, photo_limit: 5 },
  feature_flags: {},
  active_feature_slugs: [],
  loading: false,
  fetched: false,
  tenant_id: null,
  fetched_at: null,
};

export function EntitlementProvider({ children }) {
  const [entitlements, setEntitlements] = useState(DEFAULT_STATE);
  const fetchingRef = useRef(false);

  const fetchEntitlements = useCallback(async (force = false) => {
    // Prevent duplicate fetches
    if (fetchingRef.current && !force) return;
    if (entitlements.fetched && !force) return;
    
    fetchingRef.current = true;
    setEntitlements(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await fetch(`${API}/entitlements/check`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setEntitlements({
          ...data,
          loading: false,
          fetched: true,
        });
        console.log("[Entitlements] Fetched:", data.entitled_slugs, "at", data.fetched_at);
      } else {
        // Not authenticated - use defaults
        setEntitlements({ ...DEFAULT_STATE, loading: false, fetched: true });
      }
    } catch (error) {
      console.error("Failed to fetch entitlements:", error);
      setEntitlements({ ...DEFAULT_STATE, loading: false, fetched: true });
    } finally {
      fetchingRef.current = false;
    }
  }, [entitlements.fetched]);

  /**
   * Check if the current tenant is entitled to a feature
   * (owns it via subscription or bundle)
   */
  const isEntitled = useCallback(
    (featureSlug) => {
      return entitlements.entitled_slugs?.includes(featureSlug) || false;
    },
    [entitlements.entitled_slugs]
  );

  /**
   * Check if a feature is globally active (from the feature catalog)
   * This is THE SINGLE SOURCE OF TRUTH for feature flags
   */
  const isFeatureActive = useCallback(
    (featureSlug) => {
      // If we haven't fetched yet, assume feature is active (optimistic)
      if (!entitlements.fetched) return true;
      return entitlements.active_feature_slugs?.includes(featureSlug) || false;
    },
    [entitlements.active_feature_slugs, entitlements.fetched]
  );

  /**
   * Get feature flag details (name, type, limits, etc.)
   */
  const getFeatureFlag = useCallback(
    (featureSlug) => {
      return entitlements.feature_flags?.[featureSlug] || null;
    },
    [entitlements.feature_flags]
  );

  /**
   * Get usage limit (respects unlimited if entitled)
   */
  const getLimit = useCallback(
    (limitType) => {
      const limit = entitlements.limits?.[limitType];
      if (limit === -1) return Infinity;
      return limit || (limitType === 'job_limit' ? 10 : 5);
    },
    [entitlements.limits]
  );

  /**
   * Check if limit UI should be shown
   * Returns false if Unlimited Usage feature is globally inactive
   */
  const shouldShowLimitUI = useCallback(() => {
    // If we haven't fetched yet, show limit UI by default
    if (!entitlements.fetched) return true;
    return isFeatureActive("unlimited_usage");
  }, [isFeatureActive, entitlements.fetched]);

  /**
   * Check if upgrade CTA should be shown for a feature
   * Only show if feature is globally active AND user doesn't own it
   */
  const shouldShowUpgradeCTA = useCallback(
    (featureSlug) => {
      if (!entitlements.fetched) return false;
      return isFeatureActive(featureSlug) && !isEntitled(featureSlug);
    },
    [isFeatureActive, isEntitled, entitlements.fetched]
  );

  /**
   * Check if tenant has Spanish Translation entitlement
   * Used to gate field-use workflows requiring translation
   */
  const hasSpanishTranslation = useCallback(() => {
    return isEntitled("spanish_translation");
  }, [isEntitled]);

  const refresh = useCallback(() => {
    fetchEntitlements(true);
  }, [fetchEntitlements]);

  const value = {
    ...entitlements,
    isEntitled,
    isFeatureActive,
    getFeatureFlag,
    getLimit,
    shouldShowLimitUI,
    shouldShowUpgradeCTA,
    hasSpanishTranslation,
    refresh,
    fetchEntitlements,
  };

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlements() {
  const context = useContext(EntitlementContext);
  if (!context) {
    throw new Error("useEntitlements must be used within an EntitlementProvider");
  }
  return context;
}

/**
 * Hook that auto-fetches entitlements when used
 * Use this inside authenticated components
 */
export function useEntitlementsWithFetch() {
  const context = useEntitlements();
  
  useEffect(() => {
    if (!context.fetched && !context.loading) {
      context.fetchEntitlements();
    }
  }, [context]);
  
  // Add refetch alias for convenience
  return {
    ...context,
    refetch: context.fetchEntitlements,
  };
}

/**
 * Hook to check if a specific feature is globally active
 * Use this as the single source of truth for feature flags
 */
export function useFeatureFlag(featureSlug) {
  const { isFeatureActive, getFeatureFlag, loading, fetched, fetchEntitlements } = useEntitlements();
  
  useEffect(() => {
    if (!fetched && !loading) {
      fetchEntitlements();
    }
  }, [fetched, loading, fetchEntitlements]);
  
  return {
    isActive: isFeatureActive(featureSlug),
    flag: getFeatureFlag(featureSlug),
    loading: loading || !fetched,
  };
}

/**
 * Hook for limit-related UI
 * Automatically hides if Unlimited Usage feature is globally inactive
 */
export function useLimitUI() {
  const { shouldShowLimitUI, getLimit, isEntitled, loading, fetched, fetchEntitlements } = useEntitlements();
  
  useEffect(() => {
    if (!fetched && !loading) {
      fetchEntitlements();
    }
  }, [fetched, loading, fetchEntitlements]);
  
  const showLimitUI = shouldShowLimitUI();
  const hasUnlimited = isEntitled("unlimited_usage");
  
  return {
    showLimitUI,
    hasUnlimited,
    jobLimit: showLimitUI ? getLimit("job_limit") : null,
    photoLimit: showLimitUI ? getLimit("photo_limit") : null,
    loading: loading || !fetched,
  };
}

export default EntitlementContext;
